/* eslint-disable lingui/no-unlocalized-strings */

import { CheckCircle } from "@phosphor-icons/react";

import { getTierLabel } from "@/client/pages/coach/optimize/coach-live-score";
import { useCoachHasJobContext } from "@/client/stores/coach-optimization";

import { CoachScoreDonut } from "./coach-score-donut";
import { useCoachLiveScore } from "./use-coach-live-score";

/**
 * The match score for the job being optimized for: where it is now, and where the suggestions
 * can take it.
 *
 * ONE donut, not two rings. The full-stroke band is the score as it stands — it grows as suggestions
 * are accepted, which is the panel's whole job — and the thin band continuing from it is the headroom
 * the remaining suggestions represent, coloured by the tier the résumé would land in if they were all
 * accepted. So a student at 10% sees red, with a thin yellow arc reaching out to 52%; were 80% on
 * offer, that reach would be green. Both arcs use the student-side hex exactly — see
 * PROJECTED_BAND_STROKE_RATIO for why the reach is not faded instead.
 *
 * ⚠️ PROJECTED, NOT FINAL. The authoritative score is recomputed by the LMS from the saved résumé
 * after Confirm. The projection is clamped to the ceiling the LMS sent, because only two of the six
 * score components respond to résumé edits and the donut must not imply the rest are in play.
 *
 * Docs: lms-backend/docs/plans/2026-09-23-coach-resume-improvement.md
 */
export const CoachScorePanel = () => {
  const projection = useCoachLiveScore();
  const hasJobContext = useCoachHasJobContext();

  if (!projection) return null;

  // `projected` is baseline + the gains of everything accepted so far; `potential` is baseline +
  // every suggestion. So the full-stroke band tracks accepts and the thin one is still unclaimed.
  const current = projection.projected;
  const reachable = Math.max(current, projection.potential);
  const hasHeadroom = Math.round(reachable) > Math.round(current);

  /**
   * Is there nothing left to accept?
   *
   * ⚠️ COUNTED, NOT INFERRED FROM THE SCORE. This used to be spelled `!hasHeadroom`, which is a
   * statement about the DONUT — "the two arcs are the same length once rounded" — and only
   * coincidentally about the plan. The two come apart at the edges: a suggestion worth less than half
   * a point leaves no visible headroom while its card still sits unaccepted, so the panel would
   * announce the student was finished while it wasn't.
   *
   * `totalCount` already counts only the suggestions that carry an attributed gain (see
   * use-coach-live-score.ts, where `allSuggestionIds` is filtered), so this cannot be thrown off by
   * the narrative rewrites the LMS scorer never sees.
   */
  const allAccepted = projection.totalCount > 0 && projection.acceptedCount >= projection.totalCount;

  return (
    // Donut only: no heading, no card chrome, no legend. The rail is narrow and every caption
    // removed from it was restating what the shape and the centre number already say.
    <section id="coach-score" className="grid gap-y-3">
      <CoachScoreDonut
        current={current}
        projected={reachable}
        label={getTierLabel(current)}
        // "Stretch Role" is a verdict on a student against a job. The dashboard flow scores a résumé
        // against a job description, with no student profile in it, so the tier has nothing to say
        // there — the label is dropped and only the percentage remains.
        showLabel={hasJobContext}
      />

      {/* An instruction, not a tally. This replaced "+N from M accepted change(s)", which only
          appeared AFTER the first accept — so the student who had not worked out that the cards drive
          the number got no prompt at all, and the one who had was being told what they could already
          see in the donut. Shown while anything is still unaccepted, in both flows. */}
      {!allAccepted && hasHeadroom && (
        <p className="text-center text-[11px] leading-relaxed text-foreground/60">
          Accept the AI suggested changes to increase score
        </p>
      )}

      {/* The finished state, and it now EXISTS IN BOTH FLOWS. It used to be gated on `hasJobContext`,
          so a dashboard optimization that had accepted everything simply lost its caption: the
          instruction disappeared on the final click and nothing replaced it, leaving a bare donut and
          no confirmation that the student had reached the end of the plan. The prompt and its
          resolution belong together — a screen that asks for something has to say when it has got it.

          Said as a completion, not a tally: "3 of 3 accepted" makes the student check the arithmetic,
          which is the donut's job. */}
      {allAccepted && (
        <div className="grid gap-y-1">
          <p className="flex items-center justify-center gap-x-1.5 text-center text-[11px] font-medium leading-relaxed text-foreground/75">
            {/* Plain weight and no accent colour. The panel carries no chrome by design, and a green
                tick here would be a fourth colour competing with a donut whose three are already
                carrying meaning. */}
            <CheckCircle size={13} weight="fill" className="shrink-0" />
            All AI suggested changes accepted
          </p>

          {/* The ceiling explanation, which is the part that really is LMS-only.

              It says the SUGGESTIONS are exhausted, not what the scoring model's arithmetic ceiling
              is. Showing "highest reachable: 80%" right after "reach 42%" read as a contradiction and
              invited the obvious question — if 80 is reachable, where are the other suggestions? There
              are none: the remaining distance needs experience this résumé does not show.

              Withheld from the dashboard flow because that remaining distance is the fixed half of a
              job-match score — compensation, location, experience band — and none of it exists in the
              résumé-fit score that flow shows, where the ceiling is 100 and every point really is
              reachable. */}
          {/* {hasJobContext && (
            <p className="text-center text-[11px] leading-relaxed text-foreground/50">
              Going higher needs experience this resume doesn&apos;t yet show.
            </p>
          )} */}
        </div>
      )}
    </section>
  );
};
